import { List, type Map } from "immutable";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import React, { useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import { ApproverTypeUser } from "components/common/ApproverTypeUser";
import { DirectoryGroupTooltip } from "components/common/DirectoryGroupTooltip";
import { TicketingIntegrationTicketChip } from "components/common/TicketingIntegrationTicketChip";
import { TicketStatusWithIcon } from "components/common/TIcketStatusWithIcon";
import { ViewErrorButton } from "components/common/ViewErrorButton";
import { AuditLogUser } from "components/pages/AuditLogsPage/components/AuditLogUser";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useBundles } from "hooks/useBundles";
import { useIntegrations } from "hooks/useIntegrations";
import { useMultiUsers } from "hooks/useMultiUsers";
import { TicketCommentModel } from "models/TicketCommentModel";
import { sortByCreatedAt } from "utils/sortUtils";
import { useStyles } from "./styles";
import { TICKET_AUDIT_LOGS_WITH_POSSESSIVE_FORM, TRANSLATION_PREFIX } from "./TicketActivity.constants";
import type { ParseKeys, TFunction } from "i18next";
import type { TransProps } from "react-i18next";
import type { TFullTicket } from "components/common/RequestDetails";
import type { TicketAuditLogModel } from "models/auditLogs";
import type { BundleModel } from "models/BundleModel";
import type { IntegrationModel } from "models/IntegrationModel";
import type { TicketApprovalEntityModel } from "models/TicketApprovalEntityModel";
import type { UserModel } from "models/UserModel";
import type {
	ICommentActivity,
	IAuditLogActivity,
	TActivity,
	TReasonWontRevoked,
	TTranslationTypes
} from "./TicketActivity.types";

export function isManualAuditLog(auditLog: TicketAuditLogModel): auditLog is TicketAuditLogModel & {
	action: "AccessRequestPermissionGrantedSuccess" | "AccessRequestPermissionExpiredRevokedSuccess";
} {
	return (
		(auditLog.action === "AccessRequestPermissionGrantedSuccess" ||
			auditLog.action === "AccessRequestPermissionExpiredRevokedSuccess") &&
		auditLog.data.isManual
	);
}

export const getRedirectToId = (log: TicketAuditLogModel) =>
	log.action === "AccessRequestRedirectedByApprover" ? log.data.toUser.id : undefined;

// Trans requires a self closing component, so we use this one to wrap everything
const TransContent: FC<{ content?: React.ReactNode | null }> = ({ content }) => <>{content || null}</>;
const TC = TransContent;

// this function will extract audit logs and comments to a single array sorted by creation time
export const getActivities = ({ auditLogs, comments }: TFullTicket): TActivity[] => {
	if (auditLogs.size === 0 && comments.size === 0) return [];

	const sortedAuditLogs = sortByCreatedAt(auditLogs.toArray());
	const sortedComments = sortByCreatedAt(comments.toArray());
	const firstAuditLogIndex = sortedAuditLogs.findIndex(auditLog => auditLog.action === "AccessRequestCreated");
	const [firstAuditLog] = sortedAuditLogs.splice(firstAuditLogIndex, 1);
	// since we are sorting by createdAt, the first comment (justification) will be the oldest one
	const firstCommentIndex = sortedComments.findIndex(comment => !comment.isDeleted);
	const [firstComment] = sortedComments.splice(firstCommentIndex, 1);
	const firstActivity: TActivity = {
		type: "auditLogAndComment",
		data: { auditLog: firstAuditLog, comment: firstComment },
		cantDelete: true
	};
	const activities: Array<ICommentActivity | IAuditLogActivity> = sortByCreatedAt([
		...sortedAuditLogs,
		...sortedComments
	]).map(data => (data instanceof TicketCommentModel ? { type: "comment", data } : { type: "auditLog", data }));

	return [firstActivity, ...activities];
};

export const TextUser: FC<{ user: UserModel; possessiveForm?: boolean; entity?: TicketApprovalEntityModel }> = ({
	user,
	possessiveForm,
	entity,
	className
}) => {
	const { t } = useTranslation();
	const { user: currentUser } = useAuthenticatedUser();
	if (!user) return null;

	let content = user.fullName;

	if (currentUser && currentUser.id === user.id) {
		content = possessiveForm ? t(`${TRANSLATION_PREFIX}your`) : t(`${TRANSLATION_PREFIX}you`);
	} else if (possessiveForm) {
		content += t(`${TRANSLATION_PREFIX}possessive`);
	}

	return (
		<ApproverTypeUser noWrap bold content={content} ticketApprovalEntity={entity} user={user} className={className} />
	);
};

function getManualActionFromData(log: TicketAuditLogModel, users: Map<string, UserModel>) {
	const isManual = isManualAuditLog(log);
	if (!isManual) return null;

	const manualActionUserId = log.data.manualActionUserId;
	return manualActionUserId && users.has(manualActionUserId) ? (
		<TextUser user={users.get(manualActionUserId)!} />
	) : (
		<TC />
	);
}

function getRedirectFromData(
	log: TicketAuditLogModel,
	users: Map<string, UserModel>,
	t: TFunction,
	approvalEntities?: List<TicketApprovalEntityModel>
) {
	const redirectUserId = getRedirectToId(log);
	const redirectUser = redirectUserId ? users.get(redirectUserId) : null;

	return {
		redirectTarget: <AuditLogUser user={redirectUser ?? redirectUserId} onEmpty="unknown" />,
		redirectByEntity: redirectUserId
			? approvalEntities?.find(entity => entity.approvers.some(approver => approver.userIds.includes(redirectUserId)))
			: undefined
	};
}

function getForwardFromData(log: TicketAuditLogModel, users: Map<string, UserModel>, t: TFunction) {
	if (log.action === "AccessRequestForwarded") {
		const { fromUser, toUser } = log.data;
		const forwardedFromUser = fromUser ? users.get(fromUser.id) : null;
		const forwardedToUser = toUser ? users.get(toUser.id) : null;

		return {
			forwardedFrom: <AuditLogUser user={forwardedFromUser ?? fromUser?.id} onEmpty="unknown" />,
			forwardedTo: <AuditLogUser user={forwardedToUser ?? toUser?.id} onEmpty="unknown" />
		};
	}

	return {};
}

function getUserFromData({
	approvalEntities,
	log,
	redirectByEntity,
	translationPath,
	users
}: {
	approvalEntities?: List<TicketApprovalEntityModel>;
	log: TicketAuditLogModel;
	redirectByEntity?: TicketApprovalEntityModel;
	translationPath: TTranslationTypes;
	users: Map<string, UserModel>;
}) {
	const userAction =
		translationPath.startsWith("AccessRequestApproverApproved") ||
		translationPath.startsWith("AccessRequestApproverDeclined");
	const logUser = log.executerUserId ? users?.get(log.executerUserId) : null;
	let user = logUser ? <TextUser user={logUser} /> : <TC />;

	if (log.executerUserId) {
		const approverEntity = userAction
			? approvalEntities?.find(entity =>
					entity.approvers.some(approver => approver.userIds.includes(log.executerUserId!))
				)
			: null;
		if (redirectByEntity && redirectByEntity.type !== "User")
			user = logUser ? <TextUser user={logUser} entity={redirectByEntity} /> : <TC />;
		else if (approverEntity && approverEntity.type !== "User")
			user = logUser ? <TextUser user={logUser} entity={approverEntity} /> : <TC />;
	}
	return user;
}

function getTranslationPath(log: TicketAuditLogModel, manualEvent: boolean): TTranslationTypes {
	const action = log.action;

	if (log.action === "AccessRequestApproverApproved") {
		if (log.data.admin) return "AccessRequestApproverApproved.admin";
		return `AccessRequestApproverApproved.${!log.executerUserId && "webhookName" in log.data ? "webhook" : "normal"}`;
	} else if (action === "AccessRequestApproverDeclined") {
		if (log.data.admin) return "AccessRequestApproverDeclined.admin";
		return `AccessRequestApproverDeclined.${!log.executerUserId && "webhookName" in log.data ? "webhook" : "normal"}`;
	} else if (action === "AccessRequestPermissionGrantedSuccess") {
		return `AccessRequestPermissionGrantedSuccess.${manualEvent ? "manual" : "normal"}`;
	} else if (action === "AccessRequestPermissionExpiredRevokedSuccess") {
		return `AccessRequestPermissionExpiredRevokedSuccess.${manualEvent ? "manual" : "normal"}`;
	} else if (action === "AccessRequestTaskCreated") {
		return `AccessRequestTaskCreated.${log.data.taskType === "revert" ? "revert" : "normal"}`;
	}

	return action as TTranslationTypes;
}

function getExpiredRoleData(log: TicketAuditLogModel, t: TFunction) {
	if (log.action === "AccessRequestTaskCreated" && log.data.expiredRoleName) return log.data.expiredRoleName;
	return t(`${TRANSLATION_PREFIX}unknownRole`);
}

function getReasonNotRevoked(log: TicketAuditLogModel, t: TFunction) {
	if (log.action === "AccessRequestPermissionWontRevoke") {
		const reason = log.data.reason;
		if (reason) {
			return t(`common.wontRevokeReason.${reason as TReasonWontRevoked}`);
		}
	}
	return t("common.wontRevokeReason.permissionDoesNotExist");
}

function getTaskFromData(log: TicketAuditLogModel, users: Map<string, UserModel>, t: TFunction) {
	if (log.action === "AccessRequestTaskCreated") {
		const { taskType = t(`${TRANSLATION_PREFIX}unknownTaskType`), assignedUserId } = log.data;
		const assignedUser = assignedUserId ? users?.get(assignedUserId) : null;
		return { taskType, taskAssignedUser: <AuditLogUser user={assignedUser ?? assignedUserId} onEmpty="unknown" /> };
	}

	return {};
}

export const AuditLogContent: FC<{ log: TicketAuditLogModel; ticket: TFullTicket }> = ({ log, ticket }) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const integrations = useIntegrations(true);
	const bundles = useBundles(true);
	const userIds = useMemo(() => {
		const userIds = new Set<string>([ticket.creatorId, ticket.receiverId]);
		if (log.executerUserId) userIds.add(log.executerUserId);
		if (
			log.action === "AccessRequestPermissionGrantedSuccess" ||
			(log.action === "AccessRequestPermissionExpiredRevokedSuccess" && log.data.manualActionUserId)
		) {
			userIds.add(log.data.manualActionUserId!);
		}
		const redirectTo = getRedirectToId(log);
		if (redirectTo) userIds.add(redirectTo);
		if (log.action === "AccessRequestForwarded") {
			if (log.data.fromUser?.id) userIds.add(log.data.fromUser.id);
			if (log.data.toUser?.id) userIds.add(log.data.toUser.id);
		}
		if (log.action === "AccessRequestTaskCreated" && log.data.assignedUserId) {
			userIds.add(log.data.assignedUserId);
		}
		return Array.from(userIds);
	}, [log, ticket.creatorId, ticket.receiverId]);
	const users = useMultiUsers(userIds);

	const props = useMemo(
		() => (users ? getTransProps({ log, ticket, t, integrations, bundles, classes, users }) : null),
		[log, ticket, t, integrations, bundles, classes, users]
	);

	return props ? <Trans t={t} {...props} /> : <></>;
};

const getTransProps = ({
	log,
	ticket,
	t,
	integrations,
	users,
	bundles,
	classes
}: {
	log: TicketAuditLogModel;
	ticket: TFullTicket;
	t: TFunction;
	users: Map<string, UserModel>;
	integrations: Map<string, IntegrationModel> | null;
	bundles: Map<string, BundleModel> | null;
	classes: ReturnType<typeof useStyles>;
}): TransProps<ParseKeys> => {
	const manualUser = getManualActionFromData(log, users);
	const translationPath = getTranslationPath(log, manualUser !== null);
	const approvalEntities = ticket.approvalRequests.flatMap(
		approvalRequest => approvalRequest.approvalEntities || List<TicketApprovalEntityModel>()
	);
	const { redirectTarget, redirectByEntity } = getRedirectFromData(log, users, t, approvalEntities);
	const { taskType, taskAssignedUser } = getTaskFromData(log, users, t);
	const user = getUserFromData({ log, users, translationPath, redirectByEntity, approvalEntities });
	const statusWithIcon = "toStatus" in log.data ? <TicketStatusWithIcon status={log.data.toStatus} /> : <TC />;
	const viewError = "errorMessage" in log.data ? <ViewErrorButton error={log.data.errorMessage} /> : <TC />;
	const reasonWontRevoke = getReasonNotRevoked(log, t);
	const { integrationName, resourceName, roleName } = getTicketAuditLogRoleData(log, t);
	const expiredRoleName = getExpiredRoleData(log, t);
	const { forwardedFrom, forwardedTo } = getForwardFromData(log, users, t);

	const receiver = users.has(ticket.receiverId) ? (
		<TextUser
			user={users.get(ticket.receiverId)!}
			possessiveForm={!!log.action && TICKET_AUDIT_LOGS_WITH_POSSESSIVE_FORM.includes(log.action)}
		/>
	) : (
		<TC />
	);

	const emptyGroup =
		log.action === "AccessRequestRedirectedToAdminEmptyGroup" && log.data.groupName ? (
			<DirectoryGroupTooltip className={classes.auditLogEntity} groupName={log.data.groupName} />
		) : (
			<TC content={t(`${TRANSLATION_PREFIX}unknownGroup`)} />
		);

	const thirdPartyTicket = ticket.ticketingIntegrationTicket ? (
		<TicketingIntegrationTicketChip
			size="small"
			className={classes.ticketingIntegrationTicketChip}
			ticketingIntegrationTicket={ticket.ticketingIntegrationTicket}
		/>
	) : (
		<TC />
	);

	// Empty components in Trans when needed will throw unidentified error,
	// by omitting nulls and undefined it will instead just show the component name ('<user />')
	const components = omitBy(
		{
			receiver,
			user,
			redirectTarget,
			manualUser,
			emptyGroup,
			statusWithIcon,
			taskAssignedUser,
			forwardedFrom,
			forwardedTo,
			viewError,
			thirdPartyTicket,
			bold: <b />
		},
		isNil
	) as Record<string, React.JSX.Element>;
	const values = { integrationName, resourceName, roleName, expiredRoleName, reasonWontRevoke, taskType };

	if (!translationPath) return { i18nKey: "shared.emptyString" };
	if (translationPath === "AccessRequestCreated")
		return integrations && bundles
			? getCreatedParts({ ticket, auditLog: log, t, users, integrations, bundles })
			: { i18nKey: "shared.loading" };

	return { i18nKey: `${TRANSLATION_PREFIX}auditLog.${translationPath}` as const, components, values };
};

const getTicketAuditLogRoleData = (log: TicketAuditLogModel, t: TFunction) => {
	let [integrationName, resourceName, roleName] = [
		"integrationName" in log.data ? log.data.integrationName : t(`${TRANSLATION_PREFIX}unknownIntegration`),
		"resourceName" in log.data ? log.data.resourceName : t(`${TRANSLATION_PREFIX}unknownResource`),
		"roleName" in log.data ? log.data.roleName : t(`${TRANSLATION_PREFIX}unknownRole`)
	];
	if ("virtualIntegrationName" in log.data && log.data.virtualIntegrationName) {
		integrationName = log.data.virtualIntegrationName;
	}
	if ("virtualResourceName" in log.data && log.data.virtualResourceName) {
		resourceName = log.data.virtualResourceName;
	}
	if ("virtualRoleName" in log.data && log.data.virtualRoleName) {
		roleName = log.data.virtualRoleName;
	}
	return { integrationName, resourceName, roleName };
};

function getCreatedParts({
	auditLog,
	bundles,
	integrations,
	t,
	ticket,
	users
}: {
	auditLog: TicketAuditLogModel;
	bundles: Map<string, BundleModel>;
	integrations: Map<string, IntegrationModel>;
	t: TFunction;
	ticket: TFullTicket;
	users: Map<string, UserModel>;
}): TransProps<ParseKeys> {
	const ticketIntegrationResourceRole = ticket.ticketPermissions.first()?.role;
	const { targets } = ticket;
	const target = targets?.first();
	let translationValues, key: ParseKeys;
	const isBundle = targets.size === 1 && target?.type === "bundle";
	const differentUser = ticket.receiverId !== ticket.creatorId;
	const createdPrefix = `${TRANSLATION_PREFIX}auditLog.AccessRequestCreated` as const;

	if (isBundle) {
		const bundleName = target?.bundle?.name || (target?.targetId && bundles?.get(target?.targetId)?.name) || "";
		translationValues = { bundleName };
		key = differentUser
			? (`${createdPrefix}.bundleDifferentUser` as const)
			: (`${createdPrefix}.bundleSameUser` as const);
	} else if (targets.size === 1) {
		const { integrationName, resourceName, roleName } = getTicketAuditLogRoleData(auditLog, t);
		translationValues = {
			resourceName: ticketIntegrationResourceRole?.integrationResource.name || resourceName,
			roleName: ticket.ticketPermissions.first()?.role.name || roleName,
			integrationName:
				integrations.get(ticketIntegrationResourceRole?.integrationResource.integrationId || "")?.name ||
				integrationName
		};
		key = differentUser
			? (`${createdPrefix}.resourceDifferentUser` as const)
			: (`${createdPrefix}.resourceSameUser` as const);
	} else {
		translationValues = { itemsAmount: targets.size };
		key = differentUser
			? (`${createdPrefix}.multipleDifferentUser` as const)
			: (`${createdPrefix}.multipleSameUser` as const);
	}

	return {
		i18nKey: key,
		values: translationValues,
		components: {
			bold: <b />,
			creator: users?.has(ticket.creatorId) ? <TextUser user={users.get(ticket.creatorId)!} /> : <TC />,
			receiver: users?.has(ticket.receiverId) ? <TextUser user={users.get(ticket.receiverId)!} /> : <TC />
		}
	};
}
